import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Card,
  Input,
  Button,
  Select,
  Space,
  Message,
  Grid,
  InputNumber,
  Slider,
  Typography,
  Empty,
  Radio,
  Collapse,
  Drawer,
} from '@arco-design/web-react'
import { IconSend, IconDelete, IconStop, IconRefresh, IconSettings } from '@arco-design/web-react/icon'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getUserModelsApi } from '@/api/user'
import { getApiKeysApi, getApiKeySecretApi } from '@/api/apiKeys'
import { useSecureVerification } from '@/hooks/useSecureVerification'
import SecureVerifyModal from '@/components/Common/SecureVerifyModal'
import { Pill } from '@/utils/render'
import { streamChat, type StreamMessage } from '@/hooks/useStream'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { drawerWidth } from '@/utils/responsive'

const { Row, Col } = Grid
const { Group: RadioGroup } = Radio
const { Item: CollapseItem } = Collapse

interface ChatMessage extends StreamMessage {
  id: string
}

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

type PlaygroundMode = 'quick' | 'chat'
type QuickResultStatus = 'success' | 'error' | null

interface PlaygroundParamsPanelProps {
  tokenId: number | null
  setTokenId: (v: number | null) => void
  tokenOptions: Array<{ label: string; value: number }>
  model: string
  setModel: (v: string) => void
  modelOptions: string[]
  systemPrompt: string
  setSystemPrompt: (v: string) => void
  temperature: number
  setTemperature: (v: number) => void
  topP: number
  setTopP: (v: number) => void
  maxTokens: number
  setMaxTokens: (v: number) => void
  streamMode: boolean
  setStreamMode: (v: boolean) => void
}

function PlaygroundParamsPanel({
  tokenId,
  setTokenId,
  tokenOptions,
  model,
  setModel,
  modelOptions,
  systemPrompt,
  setSystemPrompt,
  temperature,
  setTemperature,
  topP,
  setTopP,
  maxTokens,
  setMaxTokens,
  streamMode,
  setStreamMode,
}: PlaygroundParamsPanelProps) {
  const { t } = useTranslation()

  return (
    <Space direction="vertical" size="medium" style={{ width: '100%' }}>
      <div>
        <Typography.Text className="text-[13px] font-semibold text-[var(--console-text-h)]">
          {t('tt.playground.configSection')}
        </Typography.Text>
      </div>
      <div>
        <Typography.Text className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.apiKey')}</Typography.Text>
        <Select
          placeholder={t('tt.playground.selectApiKey')}
          value={tokenId ?? undefined}
          onChange={setTokenId}
          options={tokenOptions}
          allowClear
          style={{ width: '100%', marginTop: 4 }}
        />
      </div>
      <div>
        <Typography.Text className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.model')}</Typography.Text>
        <Select
          placeholder={t('tt.playground.selectModel')}
          value={model}
          onChange={setModel}
          options={modelOptions.map((m) => ({ label: m, value: m }))}
          showSearch
          style={{ width: '100%', marginTop: 4 }}
        />
      </div>

      <Collapse bordered={false} defaultActiveKey={[]}>
        <CollapseItem
          name="adv"
          header={<span className="text-sm font-medium text-[var(--console-text-h)]">{t('tt.playground.advancedParams')}</span>}
        >
          <Space direction="vertical" size="medium" style={{ width: '100%' }}>
            <div>
              <Typography.Text className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.systemPromptLabel')}</Typography.Text>
              <Input.TextArea
                placeholder={t('tt.playground.systemPrompt')}
                value={systemPrompt}
                onChange={setSystemPrompt}
                autoSize={{ minRows: 2, maxRows: 6 }}
                className="!mt-1 arco-textarea-borderless focus:!shadow-none"
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <Typography.Text className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.temperature')}</Typography.Text>
                <Pill variant="info">{temperature.toFixed(2)}</Pill>
              </div>
              <Slider min={0} max={2} step={0.01} value={temperature} onChange={(v) => setTemperature(Number(v))} />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <Typography.Text className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.topP')}</Typography.Text>
                <Pill variant="info">{topP.toFixed(2)}</Pill>
              </div>
              <Slider min={0} max={1} step={0.01} value={topP} onChange={(v) => setTopP(Number(v))} />
            </div>
            <div>
              <Typography.Text className="text-xs text-gray-500">{t('tt.playground.maxTokens')}</Typography.Text>
              <InputNumber
                value={maxTokens}
                onChange={(v) => setMaxTokens(v || 0)}
                min={1}
                max={32768}
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>
            <div>
              <Typography.Text className="text-xs text-gray-500">{t('tt.playground.stream')}</Typography.Text>
              <Select
                value={streamMode ? 'stream' : 'sync'}
                onChange={(v) => setStreamMode(v === 'stream')}
                options={[
                  { value: 'stream', label: t('tt.playground.streamMode') },
                  { value: 'sync', label: t('tt.playground.syncMode') },
                ]}
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>
          </Space>
        </CollapseItem>
      </Collapse>
    </Space>
  )
}

export default function PlaygroundPage() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<PlaygroundMode>('quick')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [model, setModel] = useState<string>('')
  const [tokenId, setTokenId] = useState<number | null>(null)
  const [temperature, setTemperature] = useState(0.7)
  const [maxTokens, setMaxTokens] = useState(2048)
  const [topP, setTopP] = useState(1)
  const [streamMode, setStreamMode] = useState(true)
  const [loading, setLoading] = useState(false)
  const [quickTtftMs, setQuickTtftMs] = useState<number | null>(null)
  const [quickTotalMs, setQuickTotalMs] = useState<number | null>(null)
  const [quickResultStatus, setQuickResultStatus] = useState<QuickResultStatus>(null)
  const [paramsOpen, setParamsOpen] = useState(false)
  const { isMobile } = useBreakpoint()

  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const modelsQuery = useQuery({ queryKey: ['user-models'], queryFn: () => getUserModelsApi() })
  const tokensQuery = useQuery({ queryKey: ['my-tokens'], queryFn: () => getApiKeysApi() })

  const { runSecure, verifyVisible, handleSuccess, handleCancel } = useSecureVerification()

  const quickPresets = useMemo(
    () => [
      { label: t('tt.playground.presetHello'), value: t('tt.playground.presetHello') },
      { label: t('tt.playground.presetStreamTest'), value: t('tt.playground.presetStreamTest') },
    ],
    [t]
  )

  const tokenOptions = useMemo(() => {
    const items = (tokensQuery.data as { items?: Array<{ id: number; name: string }> } | undefined)?.items || []
    return items.map((t) => ({ label: t.name, value: t.id }))
  }, [tokensQuery.data])

  const lastAssistantText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') return messages[i].content
    }
    return ''
  }, [messages])

  useEffect(() => {
    if (!model && modelsQuery.data && modelsQuery.data.length > 0) {
      setModel(modelsQuery.data[0])
    }
  }, [modelsQuery.data, model])

  useEffect(() => {
    if (!tokenId && tokenOptions.length > 0) {
      setTokenId(tokenOptions[0].value)
    }
  }, [tokenOptions, tokenId])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  const generateFromText = async (
    rawText: string,
    baseMessages: ChatMessage[],
    opts?: { isQuick?: boolean }
  ) => {
    const isQuick = opts?.isQuick ?? (mode === 'quick')
    const text = rawText.trim()
    if (!text) return
    if (!tokenId) {
      Message.warning(t('tt.playground.selectApiKeyFirst'))
      return
    }
    if (!model) {
      Message.warning(t('tt.playground.selectModelFirst'))
      return
    }

    const userMsg: ChatMessage = { id: newId(), role: 'user', content: text }
    const assistantMsg: ChatMessage = { id: newId(), role: 'assistant', content: '' }
    const historyForApi: StreamMessage[] = []
    if (systemPrompt.trim()) historyForApi.push({ role: 'system', content: systemPrompt.trim() })
    baseMessages.forEach((m) => historyForApi.push({ role: m.role, content: m.content }))
    historyForApi.push({ role: 'user', content: text })

    setMessages([...baseMessages, userMsg, assistantMsg])
    setInput('')
    setLoading(true)

    if (isQuick) {
      setQuickTtftMs(null)
      setQuickTotalMs(null)
      setQuickResultStatus(null)
    }

    const startTime = Date.now()
    let firstDeltaSeen = false

    try {
      const keyResp = (await runSecure(() => getApiKeySecretApi(tokenId))) as { key: string }
      const apiKey = `sk-${keyResp.key}`

      const controller = new AbortController()
      abortRef.current = controller

      await streamChat({
        apiKey,
        model,
        messages: historyForApi,
        temperature,
        max_tokens: maxTokens,
        top_p: topP,
        stream: streamMode,
        signal: controller.signal,
        onDelta: (delta, full) => {
          if (isQuick && !firstDeltaSeen && (delta.length > 0 || full.length > 0)) {
            firstDeltaSeen = true
            setQuickTtftMs(Date.now() - startTime)
          }
          setMessages((prev) => prev.map((m) => (m.id === assistantMsg.id ? { ...m, content: full } : m)))
        },
        onDone: (full) => {
          if (isQuick) {
            const end = Date.now() - startTime
            setQuickTotalMs(end)
            if (!firstDeltaSeen) {
              setQuickTtftMs(end)
            }
            setQuickResultStatus('success')
          }
          setMessages((prev) => prev.map((m) => (m.id === assistantMsg.id ? { ...m, content: full } : m)))
        },
      })
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        Message.info(t('tt.playground.aborted'))
      } else {
        const msg = err instanceof Error ? err.message : t('tt.playground.requestFailed')
        Message.error(msg)
        if (isQuick) {
          setQuickTotalMs(Date.now() - startTime)
          if (!firstDeltaSeen) {
            setQuickTtftMs(null)
          }
          setQuickResultStatus('error')
        }
        setMessages((prev) => prev.map((m) => (m.id === assistantMsg.id ? { ...m, content: `${t('tt.playground.errorPrefix')} ${msg}` } : m)))
      }
    } finally {
      setLoading(false)
      abortRef.current = null
    }
  }

  const sendMessage = async () => {
    if (mode === 'quick') {
      await generateFromText(input, [], { isQuick: true })
    } else {
      await generateFromText(input, messages, { isQuick: false })
    }
  }

  const sendPreset = async (text: string) => {
    if (mode !== 'quick') {
      setInput(text)
      return
    }
    setInput(text)
    await generateFromText(text, [], { isQuick: true })
  }

  const stop = () => {
    abortRef.current?.abort()
  }

  const regenerate = async () => {
    if (loading || messages.length === 0) return
    let lastUserIndex = -1
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') {
        lastUserIndex = i
        break
      }
    }
    if (lastUserIndex < 0) {
      Message.info(t('tt.playground.noRegenerate'))
      return
    }
    const prompt = messages[lastUserIndex].content
    const baseMessages = messages.slice(0, lastUserIndex)
    await generateFromText(prompt, baseMessages, { isQuick: mode === 'quick' })
  }

  const clear = () => {
    setMessages([])
    setQuickTtftMs(null)
    setQuickTotalMs(null)
    setQuickResultStatus(null)
  }

  const showQuickMetrics = mode === 'quick' && (loading || quickResultStatus !== null)

  const paramsPanelProps: PlaygroundParamsPanelProps = {
    tokenId,
    setTokenId,
    tokenOptions,
    model,
    setModel,
    modelOptions: modelsQuery.data || [],
    systemPrompt,
    setSystemPrompt,
    temperature,
    setTemperature,
    topP,
    setTopP,
    maxTokens,
    setMaxTokens,
    streamMode,
    setStreamMode,
  }

  return (
    <>
      <Row gutter={16} className="items-stretch">
        <Col xs={24} lg={17} className="min-h-0">
          <Card
            className="tt-card !rounded-2xl min-h-[min(calc(100vh-140px),720px)] lg:h-[calc(100vh-140px)]"
            bordered={false}
            style={{ display: 'flex', flexDirection: 'column' }}
            bodyStyle={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <div className="sticky top-0 z-20 -mx-4 px-4 py-2 mb-2 bg-white/95 backdrop-blur border-b border-[var(--console-border)] flex flex-wrap items-center justify-between gap-y-2 gap-x-3 shrink-0">
              <div className="flex flex-wrap items-center gap-3 min-w-0">
                <div className="text-[15px] font-semibold text-[var(--console-text-h)] shrink-0">{t('tt.playground.runArea')}</div>
                <RadioGroup
                  type="button"
                  name="playground-mode"
                  value={mode}
                  onChange={(v) => setMode((v as PlaygroundMode) || 'quick')}
                  className="!flex-wrap"
                >
                  <Radio value="quick">{t('tt.playground.quickMode')}</Radio>
                  <Radio value="chat">{t('tt.playground.chatMode')}</Radio>
                </RadioGroup>
              </div>
              <Space wrap>
                {isMobile && (
                  <Button icon={<IconSettings />} onClick={() => setParamsOpen(true)} aria-label={t('tt.playground.params')}>
                    {t('tt.playground.params')}
                  </Button>
                )}
                <Button icon={<IconRefresh />} onClick={regenerate} disabled={messages.length === 0 || loading} aria-label={t('tt.playground.regenerate')}>
                  {t('tt.playground.regenerate')}
                </Button>
                <Button icon={<IconDelete />} onClick={clear} disabled={messages.length === 0} aria-label={t('tt.playground.clear')}>
                  {t('tt.playground.clear')}
                </Button>
                {loading && (
                  <Button icon={<IconStop />} status="warning" onClick={stop} aria-label={t('tt.playground.stop')}>
                    {t('tt.playground.stop')}
                  </Button>
                )}
              </Space>
            </div>

            {mode === 'quick' ? (
              <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
                <div className="max-w-2xl mx-auto w-full h-full min-h-0 flex flex-col justify-center items-stretch py-2">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {quickPresets.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        className="cursor-pointer px-3 py-1.5 text-[13px] rounded-md border border-[var(--console-border)] bg-white hover:border-[var(--brand-primary-mid)] hover:bg-[var(--console-hover-item)] transition-colors"
                        onClick={() => {
                          void sendPreset(p.value)
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  <div className="mb-4">
                    <div className="relative rounded-xl border border-[var(--console-border)] bg-white p-3 pb-12 shadow-sm">
                      <Input.TextArea
                        className="!border-0 !border-none !bg-transparent !shadow-none !rounded-md focus:!shadow-none arco-textarea-borderless"
                        style={{ minHeight: 100, resize: 'none' as const }}
                        placeholder={t('tt.playground.inputQuick')}
                        value={input}
                        onChange={setInput}
                        onPressEnter={(e) => {
                          if (!e.shiftKey) {
                            e.preventDefault()
                            if (!loading) void sendMessage()
                          }
                        }}
                        disabled={loading}
                      />
                      <Button
                        type="primary"
                        shape="round"
                        className="!absolute bottom-2 right-2 h-9 w-9 !p-0 min-w-0 !flex !items-center !justify-center"
                        icon={<IconSend />}
                        loading={loading}
                        disabled={loading}
                        title={t('tt.playground.send')}
                        aria-label={t('tt.playground.send')}
                        onClick={() => void sendMessage()}
                      />
                    </div>
                  </div>

                  {showQuickMetrics && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 w-full">
                      <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-page-bg)] px-3 py-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.ttft')}</span>
                        <Pill variant="neutral" className="!text-sm tabular-nums">
                          {loading && quickTtftMs === null
                            ? '…'
                            : quickTtftMs !== null
                              ? `${quickTtftMs} ms`
                              : '-'}
                        </Pill>
                      </div>
                      <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-page-bg)] px-3 py-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.totalTime')}</span>
                        <Pill variant="neutral" className="!text-sm tabular-nums">
                          {loading && quickTotalMs === null
                            ? '…'
                            : quickTotalMs !== null
                              ? `${quickTotalMs} ms`
                              : '-'}
                        </Pill>
                      </div>
                      <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-page-bg)] px-3 py-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-[var(--console-text-muted)]">{t('tt.playground.resultStatus')}</span>
                        {loading && quickResultStatus === null ? (
                          <Pill variant="cyan">{t('tt.playground.requesting')}</Pill>
                        ) : quickResultStatus === 'success' ? (
                          <Pill variant="success">{t('tt.playground.statusSuccess')}</Pill>
                        ) : quickResultStatus === 'error' ? (
                          <Pill variant="danger">{t('tt.playground.statusFailed')}</Pill>
                        ) : (
                          <span className="text-[var(--console-text-subtle)]">-</span>
                        )}
                      </div>
                    </div>
                  )}

                  {(lastAssistantText || (loading && messages.some((m) => m.role === 'assistant'))) && (
                    <div
                      className="bg-[var(--console-page-bg)] p-4 rounded-lg min-h-[120px] text-sm text-[var(--console-text)] whitespace-pre-wrap border border-[var(--console-border)]/60"
                    >
                      {lastAssistantText || (loading ? '…' : '')}
                    </div>
                  )}

                  {messages.length === 0 && !loading && !lastAssistantText && (
                    <Empty description={t('tt.playground.emptyQuick')} className="!py-4" />
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 min-h-0 flex-col gap-0">
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto min-h-0 bg-[var(--console-page-bg)] rounded-lg p-4 space-y-3 text-sm"
                >
                  {messages.length === 0 && (
                    <Empty description={t('tt.playground.emptyChat')} className="!py-20" />
                  )}
                  {messages.map((m) => (
                    <div key={m.id} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                      <div className="mb-1 text-xs text-gray-400">
                        {m.role === 'user' ? t('tt.playground.roleUser') : m.role === 'assistant' ? t('tt.playground.roleAssistant') : t('tt.playground.roleSystem')}
                      </div>
                      <div
                        className={`inline-block whitespace-pre-wrap px-3 py-2 rounded-lg max-w-[85%] text-left ${
                          m.role === 'user'
                            ? 'bg-[var(--brand-primary)] text-white'
                            : 'bg-white border border-[var(--console-border)]'
                        }`}
                      >
                        {m.content || (loading && m.role === 'assistant' ? '…' : '')}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="relative rounded-xl border border-[var(--console-border)] bg-white p-3 pb-12 mt-3 shadow-sm shrink-0">
                  <Input.TextArea
                    className="!border-0 !border-none !bg-transparent !shadow-none !rounded-lg focus:!shadow-none arco-textarea-borderless"
                    style={{ minHeight: 30, resize: 'none' as const }}
                    placeholder={t('tt.playground.inputChat')}
                    value={input}
                    onChange={setInput}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault()
                        if (!loading) void sendMessage()
                      }
                    }}
                    disabled={loading}
                  />
                  <Button
                    type="primary"
                    shape="round"
                    className="!absolute bottom-2 right-2 h-9 w-9 !p-0 min-w-0 !flex !items-center !justify-center"
                    icon={<IconSend />}
                    loading={loading}
                    disabled={loading}
                    title={t('tt.playground.send')}
                    aria-label={t('tt.playground.send')}
                    onClick={() => void sendMessage()}
                  />
                </div>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={0} lg={7} className="min-h-0 hidden lg:block">
          <Card
            className="tt-card !rounded-2xl h-[calc(100vh-140px)] min-h-0"
            bordered={false}
            style={{ display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}
            title={t('tt.playground.paramsTitle')}
          >
            <PlaygroundParamsPanel {...paramsPanelProps} />
          </Card>
        </Col>
      </Row>

      <Drawer
        visible={paramsOpen}
        title={t('tt.playground.paramsTitle')}
        onCancel={() => setParamsOpen(false)}
        footer={null}
        width={drawerWidth(360)}
        className="lg:hidden"
      >
        <PlaygroundParamsPanel {...paramsPanelProps} />
      </Drawer>

      <SecureVerifyModal
        visible={verifyVisible}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        title={t('tt.playground.verifyViewKey')}
      />
    </>
  )
}
